import {
  // AdvancedMarker, // advanced marker required setting in google cloud console
  Map,
  APIProvider,
  Marker,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps'
import { GlobalContext, type GlobalContextType } from '@/store/GlobalContext'
import { useContext, useState } from 'react'
import { CatGender, type Cat } from '@/type/cat'
import CatDetails from '@/components/modals/CatDetails'
import PlaceDetails from '@/components/modals/PlaceDetails'
import EditCatProfile from '@/components/modals/EditCatProfile'
import { Button } from '@/components/ui/button'

// Define your map styles to hide POIs/shops
const mapStyles = [
  {
    featureType: 'poi', // All points of interest
    stylers: [{ visibility: 'off' }],
  },
]

// const BlueDot = ({ position }: { position: google.maps.LatLngLiteral }) => {
//   return (
//     <AdvancedMarker position={position}>
//       {/* Blue dot SVG with CSS animation for glow effect */}
//       <div className='relative w-5 h-5'>
//         <div className='absolute inset-0 bg-blue-400 rounded-full opacity-75 animate-pulse' />
//         <div className='absolute inset-1 bg-blue-500 rounded-full' />
//       </div>
//     </AdvancedMarker>
//   )
// }

const DiscoverCat = () => {
  const [selectedCat, setSelectedCat] = useState<Cat | undefined>(undefined)
  const [mapClick, setMapClick] = useState<
    | {
        lat: number
        lng: number
      }
    | undefined
  >(undefined)
  const [center, setCenter] = useState<
    { lat: number; lng: number } | undefined
  >(undefined)
  const [zoom, setZoom] = useState<number>(17)
  const { cats, mutate: mutateCats } = useContext(GlobalContext) as GlobalContextType

  const [show, setShow] = useState<boolean>(false)
  const [showEditProfile, setShowEditProfile] = useState<boolean>(false)

  let exampleLocation = {
    lat: 18.793839328275027,
    lng: 98.96971656725239,
  }

  const handleMapClick = (e: MapMouseEvent) => {
    console.log(e)
    const newLocation = {
      lat: e.detail.latLng?.lat ?? 0,
      lng: e.detail.latLng?.lng ?? 0,
    }
    setMapClick(newLocation)
    setCenter(calculateTopCenterPosition(newLocation.lat, newLocation.lng))
  }

  const calculateTopCenterPosition = (lat: number, lng: number) => {
    // Calculate the offset based on zoom level
    // The higher the zoom, the smaller the offset needed
    const baseOffset = 0.002
    const zoomFactor = Math.pow(2, 17 - zoom) // Adjust based on default zoom of 17
    const offset = baseOffset * zoomFactor

    return {
      lat: lat - offset,
      lng: lng,
    }
  }

  const handleMarkerClick = (marker: Cat) => {
    setShow(true)
    setSelectedCat(marker)
    setCenter(
      calculateTopCenterPosition(marker.location.lat, marker.location.lng)
    )
  }

  const handleDrawerClose = () => {
    setShow(false)
    setSelectedCat(undefined)
    // setCenter(undefined)
  }

  {
    /* {exampleMarker && (
                <BlueDot
                  position={exampleMarker as google.maps.LatLngLiteral}
                />
              )} */
  }

  return (
    <main className='h-screen p-0 md:p-4'>
      <div className='relative h-full w-full rounded-lg overflow-hidden'>
        {/* starts:: map */}
        <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
          <Map
            // mapId='3082c10de1a6fa91'
            defaultCenter={exampleLocation}
            center={center}
            defaultZoom={17}
            zoom={zoom}
            onZoomChanged={(e) => setZoom(e.detail.zoom)}
            gestureHandling={'greedy'}
            disableDefaultUI
            styles={mapStyles}
            onClick={handleMapClick}
          >
            {cats.data?.map((marker: Cat, index: number) => (
              <Marker
                key={index}
                position={{
                  lat: marker.location.lat,
                  lng: marker.location.lng,
                }}
                title={marker.name}
                onClick={() => handleMarkerClick(marker)}
              />
            ))}

            {mapClick && <Marker position={mapClick} />}
          </Map>
        </APIProvider>
        {/* ends:: map */}

        {/* starts:: main action buttons */}
        <div className='absolute bottom-0 left-0 right-0 p-4 flex justify-end'>
          <Button
            variant='primary'
            className='rounded-full shadow shadow-orange-600'
            onClick={() => setShowEditProfile(true)}
          >
            I found a cat!
          </Button>
        </div>
        {/* ends:: main action buttons */}
      </div>

      {/* starts:: cat's details */}
      <CatDetails
        show={[show, setShow]}
        selectedCat={selectedCat as Cat}
        onClose={handleDrawerClose}
      />
      {/* ends:: cat's details */}

      {/* starts:: place details */}
      {mapClick && (
        <PlaceDetails
          place={mapClick}
          onClose={() => {
            setMapClick(undefined)
            setCenter(undefined)
          }}
          onCatSelect={(cat) => {
            setShow(true)
            setSelectedCat(cat)
            setCenter(
              calculateTopCenterPosition(cat.location.lat, cat.location.lng)
            )
          }}
          onFoundNewCat={() => {
            setShowEditProfile(true)
            const geocoder = new google.maps.Geocoder()

            geocoder.geocode(
              { location: { lat: mapClick.lat, lng: mapClick.lng } },
              (results, status) => {
                if (status === 'OK' && results?.[0]) {
                  setSelectedCat({
                    location: {
                      address: results[0].formatted_address,
                      lat: mapClick.lat,
                      lng: mapClick.lng,
                    },
                    name: '',
                    gender: CatGender.MALE,
                    images: [],
                    bio: '',
                    id: '',
                    color: '',
                    date_founded: new Date().toISOString(),
                  })
                }
              }
            )
          }}
        />
      )}
      {/* ends:: place details */}

      {/* starts:: edit cat profile */}
      <EditCatProfile
        show={[showEditProfile, setShowEditProfile]}
        selectedCat={mapClick ? selectedCat : undefined}
        onRequestSuccess={() => {
          // toast.success('Cat profile created successfully')
          setShowEditProfile(false)
          mutateCats()
        }}
      />
      {/* ends:: edit cat profile */}
    </main>
  )
}

export default DiscoverCat
